import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, MapPin } from "lucide-react";
import { cta } from "@/lib/site";

const container = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.12, delayChildren: 0.15 },
  },
};

const line = {
  hidden: { y: "115%" },
  show: {
    y: "0%",
    transition: { duration: 1.05, ease: [0.22, 1, 0.36, 1] },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.9 },
  },
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const y1 = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -220]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const rot = useTransform(scrollYProgress, [0, 1], [0, 6]);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden pt-28 pb-16 md:pt-36 md:pb-24"
      data-testid="hero-section"
    >
      {/* Faint corner ornaments */}
      <div className="hidden sm:flex absolute top-20 left-6 md:left-12 text-[#68655E]/60 text-[11px] uppercase tracking-[0.24em] items-center gap-2">
        <MapPin size={12} />
        First Class · Non-Machinable
      </div>
      <div className="hidden sm:block absolute top-20 right-6 md:right-12 text-[#68655E]/60 text-[11px] uppercase tracking-[0.24em]">
        Est. 2015 — Delivered by USPS
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6 relative">
        {/* SEO-critical semantic H1 — visually hidden, crawlable */}
        <h1 className="sr-only">
          Postcard Printing &amp; Mailing Service USA — Handwritten Postcard
          Marketing, Bulk Printing, EDDM, Targeted Mailing Lists, Tracking &amp;
          Automation from Postmark &amp; Co.
        </h1>

        {/* Left: kinetic headline */}
        <div className="col-span-12 lg:col-span-8 relative z-10">
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            aria-hidden="true"
            className="font-serif-display text-[#1C1C1A] text-[52px] sm:text-[76px] md:text-[92px] lg:text-[112px] leading-[0.92] tracking-[-0.02em]"
          >
            <span className="kinetic-line">
              <motion.span variants={line} className="block">
                Grow your
              </motion.span>
            </span>
            <span className="kinetic-line">
              <motion.span variants={line} className="block italic font-medium">
                <span className="text-[#D93829]">business</span> the way
              </motion.span>
            </span>
            <span className="kinetic-line">
              <motion.span variants={line} className="block">
                grandma did —
              </motion.span>
            </span>
            <span className="kinetic-line">
              <motion.span variants={line} className="block">
                by <span className="italic font-medium">mail</span>.
              </motion.span>
            </span>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="mt-10 max-w-xl"
          >
            <p className="text-[#3B3A36] text-lg md:text-xl leading-relaxed font-sans-body">
              Real postcards. Real handwriting. Real ROI. Send one card or one
              hundred thousand — from your couch — to the mailboxes of people
              who actually{" "}
              <span className="italic">read what they hold</span>.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-5">
              <a
                href={cta("hero")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-primary-cta"
                className="group relative inline-flex items-center gap-3 bg-[#D93829] text-white px-8 py-4 rounded-full text-sm uppercase tracking-[0.2em] font-semibold overflow-hidden"
              >
                <span className="relative z-10">Start sending — free</span>
                <ArrowUpRight
                  size={18}
                  className="relative z-10 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                />
                <span className="absolute inset-0 bg-[#1C1C1A] scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
              </a>

              <div className="flex items-center gap-3 text-[13px] text-[#68655E]">
                <div className="flex -space-x-2">
                  {["#D93829", "#2B3A55", "#8C6A3F"].map((c, i) => (
                    <span
                      key={i}
                      className="w-7 h-7 rounded-full border-2 border-[#F7F5F0]"
                      style={{ background: c }}
                    />
                  ))}
                </div>
                <span>
                  Trusted by <b className="text-[#1C1C1A]">40,000+</b>{" "}
                  operators, agents & founders
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right: parallax postcard cluster */}
        <div className="col-span-12 lg:col-span-4 relative h-[420px] lg:h-auto min-h-[520px]">
          <motion.div
            style={{ y: y1, rotate: rot }}
            className="absolute -top-4 right-2 lg:-right-8 w-[280px] md:w-[340px] polaroid rotate-[6deg] z-30"
          >
            <img
              src="https://images.unsplash.com/photo-1637597384611-0c33cef6ec03?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxwb3N0Y2FyZHMlMjBzdGFja2VkfGVufDB8fHx8MTc4NDIzMjY3M3ww&ixlib=rb-4.1.0&q=85"
              alt="Stacked postcards"
              className="w-full h-[240px] md:h-[280px] object-cover"
            />
            <div className="mt-3 flex items-center justify-between text-[11px] uppercase tracking-[0.2em] text-[#68655E]">
              <span>№ 001 / Real Estate</span>
              <span className="font-hand text-[15px] text-[#D93829] normal-case tracking-normal">
                — with love
              </span>
            </div>
          </motion.div>

          <motion.div
            style={{ y: y2 }}
            className="absolute top-40 lg:top-52 -left-4 lg:-left-16 w-[220px] md:w-[260px] polaroid -rotate-[8deg] z-20"
          >
            <img
              src="https://images.unsplash.com/photo-1578531504112-0dcd95eda450?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHw0fHxwb3N0YWwlMjBzdGFtcCUyMG1haWx8ZW58MHx8fHwxNzg0MjMyNjczfDA&ixlib=rb-4.1.0&q=85"
              alt="Wax seal envelope"
              className="w-full h-[180px] md:h-[220px] object-cover"
            />
            <div className="mt-3 text-[11px] uppercase tracking-[0.2em] text-[#68655E]">
              Sealed · Hand-Signed
            </div>
          </motion.div>

          <motion.div
            style={{ y: y3 }}
            className="absolute bottom-4 right-6 lg:right-16 w-[180px] md:w-[210px] polaroid rotate-[3deg] z-10"
          >
            <img
              src="https://images.unsplash.com/photo-1584382179644-9c11f8e6ee0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxwb3N0YWwlMjBzdGFtcCUyMG1haWx8ZW58MHx8fHwxNzg0MjMyNjczfDA&ixlib=rb-4.1.0&q=85"
              alt="Assorted postage stamps"
              className="w-full h-[140px] md:h-[170px] object-cover"
            />
            <div className="mt-3 text-[11px] uppercase tracking-[0.2em] text-[#68655E]">
              Postage Included
            </div>
          </motion.div>

          {/* Handwriting overlay */}
          <div className="absolute top-2 left-0 lg:left-4 -rotate-6 pointer-events-none z-40">
            <span className="font-hand text-[#D93829] text-[38px] md:text-[46px] leading-none">
              real mail. real results.
            </span>
          </div>
        </div>
      </div>

      {/* Bottom meta strip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="max-w-[1440px] mx-auto px-6 md:px-12 mt-14 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-[#E4DFD4] pt-8"
      >
        {[
          ["4.7×", "avg. response rate vs. email"],
          ["$0.90", "starts per card, postage in"],
          ["48 hrs", "printed, stamped, mailed"],
          ["No", "minimums, contracts or setup"],
        ].map(([k, v]) => (
          <div key={k}>
            <div className="font-serif-display text-3xl md:text-4xl text-[#1C1C1A]">
              {k}
            </div>
            <div className="text-[13px] uppercase tracking-[0.16em] text-[#68655E] mt-1">
              {v}
            </div>
          </div>
        ))}
      </motion.div>
    </section>
  );
};

export default Hero;
